import { Address, beginCell, Cell, Contract, Dictionary, contractAddress, ContractProvider, Sender, SendMode, toNano, address } from '@ton/core';
import { Address as TonAddress} from 'ton'
import BN from "bn.js";
import {Cell as TonCell} from "ton"
import { JettonWallet } from '@ton/ton';
import { getClient } from 'lib/get-ton-client';
import { makeGetCall } from 'lib/make-get-call';
export type StakeContent = {
    type: 0 | 1,
    uri: string
};

export type StakeConfig = {
    admin : Address,
    stake_token_address : Address,
    reward_token_address : Address,
    total_staked : number,
    reward_per_second : number,
    deposit_fee : number,
    claim_fee : number,
    start_date : number,
    user_wallet_code : Cell,
    stake_token_code : Cell,
    reward_token_code : Cell

};



export type StakeWalletConfig = {ownerAddress: Address, stakeAddress: Address, walletCode: Cell}
export function stakeContractConfigToCell(config: StakeConfig): Cell {
    return beginCell()
        .storeAddress(config.admin)
        .storeAddress(config.stake_token_address)
        .storeAddress(config.reward_token_address)
        .storeRef(
            beginCell()
            .storeCoins(config.total_staked)
            .storeCoins(config.reward_per_second)
            .storeUint(config.deposit_fee, 32)
            .storeUint(config.claim_fee, 32)
            .storeUint(config.start_date, 32)
            .endCell()
        )
        .storeCoins(0)
        .storeCoins(0)
        .storeRef(config.user_wallet_code)
        .storeRef(config.stake_token_code)
        .storeRef(config.reward_token_code)
        .endCell();
}

function stakeAddressConfigToCell(config: StakeConfig): Cell {
    return beginCell()
    .storeAddress(config.admin)
    .storeAddress(config.stake_token_address)
    .storeAddress(config.reward_token_address)
    .endCell()
}

export function stakeWalletMinterConfigToCell(config: StakeWalletConfig): Cell {
    return beginCell()
        .storeAddress(config.ownerAddress)
        .storeAddress(config.stakeAddress)
        .storeCoins(0)
        .storeCoins(0)
        .endCell()
}


export class Stake implements Contract {
    constructor(readonly address: Address, readonly init?: { code: Cell; data: Cell }) { }

    static createFromAddress(address: Address) {
        return new Stake(address);
    }

    static createFromConfig(config: StakeConfig, code: Cell, workchain = 0) {
        const data = stakeContractConfigToCell(config);
        const init = { code, data };
        return new Stake(contractAddress(workchain, init), init);
    }

    static createWalletAddressFromConfig(config: StakeWalletConfig, code: Cell, workchain = 0) {
        const data = stakeWalletMinterConfigToCell(config);
        const init = {code, data};
        return new Stake(contractAddress(workchain, init))
    }

    async sendDeploy(provider: ContractProvider, via: Sender, value: bigint) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY
        });
    }

    static initializeMessage(newOwner: Address) {
        return beginCell().storeUint(0xbe5a7595, 32).storeUint(0, 64) // op, queryId
            .storeAddress(newOwner)
            .endCell();
    }

    async sendInitialize(provider: ContractProvider, via: Sender) {
        if(via.address) {

            await provider.internal(via, {
                sendMode: SendMode.PAY_GAS_SEPARATELY,
                body: Stake.initializeMessage(via.address),
                value: toNano("0.1"),
            });
        }
    }

    static changeAdminMessage(newOwner: Address) {
        return beginCell().storeUint(0x4840664f, 32).storeUint(0, 64) // op, queryId
            .storeAddress(newOwner)
            .endCell();
    }

    async sendChangeAdmin(provider: ContractProvider, via: Sender, newOwner: Address) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Stake.changeAdminMessage(newOwner),
            value: toNano("0.1"),
        });
    }


    static stateMessage(state: boolean) {
        return beginCell().storeUint(0x58ca5361, 32).storeUint(0, 64) // op, queryId
            .storeBit(state)
            .endCell();
    }

    async sendChangeState(provider: ContractProvider, via: Sender, state: boolean) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Stake.stateMessage(state),
            value: toNano('0.2')
        });

    }

    static withdrawMessage() {
        return beginCell().storeUint(0x46ed2e94, 32).storeUint(0, 64).storeCoins(toNano(0.1)) // op, queryId
            .endCell();
    }

    async sendWithdraw(provider: ContractProvider, via: Sender) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Stake.withdrawMessage(),
            value: toNano('0.1')
        });

    }

    static depositMessage(from: Address, to:Address, jettonAmount: bigint) {
        return  beginCell()
        .storeUint(0xf8a7ea5, 32)
        .storeUint(1, 64)
        .storeCoins(jettonAmount)
        .storeAddress(to)
        .storeAddress(from)
        .storeBit(0)
        .storeCoins(toNano(0.3))
        .storeBit(1) // forward_payload in this slice, not separate cell
        .storeRef(
            beginCell()
            .storeUint(0x8fac0e7b, 32)
            .storeCoins(jettonAmount)
            .storeAddress(from)
            .endCell()
        )
        .endCell();
    }

    async sendDeposit(provider: ContractProvider, via: Sender, to: Address, value: bigint) { 
        // const to = address(process.env.STAKING_ADDRESS ?? "")   
       
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Stake.depositMessage(via.address ?? address(""), to, value),
            value: toNano("0.5")
        });

    }

      parseDict(d: any) {
            if (d) {
              return d.beginParse().loadDictDirect(Dictionary.Keys.Address(), Dictionary.Values.BigInt(64)).values();
            }
            return Dictionary.empty(Dictionary.Keys.Address(), Dictionary.Values.BigInt(64)).values();
          }

    // async getJettonData(provider: ContractProvider) {
        
    //     let res = await provider.get('get_stake_data', []);
    //     let start_date = res.stack.readBigNumber();
    //     let accTokenPerShare = res.stack.readNumber();
    //     let lastRewardTime = res.stack.readNumber();
    //     let claim_fee = res.stack.readNumber();
    //     let deposit_fee = res.stack.readNumber();
    //     let total_staked = res.stack.readBigNumber();
    //     let reward_per_second = res.stack.readBigNumber();
    //     let stake_token_address = res.stack.readAddress();
    //     let reward_token_address = res.stack.readAddress();
    //     return {
    //         start_date,
    //         accTokenPerShare,
    //         lastRewardTime,
    //         claim_fee,
    //         deposit_fee,
    //         total_staked,
    //         reward_per_second,
    //         stake_token_address,
    //         reward_token_address
    //     };
    // }

    getJettonData =  async (presaleWalletAddress: any) => {
        const tc = await getClient();
        const contractAddr = TonAddress.parse(presaleWalletAddress)
        const minter = await makeGetCall(
            contractAddr,
            "get_stake_data",
            [],
            async ([start_date, accTokenPerShare, lastRewardTime, claim_fee, deposit_fee, total_staked, reward_per_second, stake_token_address, reward_token_address]) => ({
                start_date : Number(start_date),
                accTokenPerShare : accTokenPerShare?.toString(),
                lastRewardTime : lastRewardTime?.toString(),
                claim_fee : Number(claim_fee),
                deposit_fee : Number(deposit_fee),
                total_staked : total_staked?.toString(),
                reward_per_second : reward_per_second?.toString(),
                stake_token_address : stake_token_address,
                reward_token_address : reward_token_address                   
            }),
            tc,
          );
          
          return minter;
        }

    async getUserStakeWalletData(stakeWalletAddress: any) {
        const tc = await getClient();
        const contractAddr = TonAddress.parse(stakeWalletAddress);
        const userData = await makeGetCall(
            contractAddr,
            "get_jetton_data",
            [],
            async ([staked_amount, reward_debt, user_address]) => ({
                staked_amount: staked_amount?.toString(),
                reward_debt: reward_debt?.toString(),
                user_address
            }),
            tc,
          );
          
          return userData;
        }
        
}


